import React, { useState, useEffect } from 'react'
import axios from 'axios'
import TableComponent from '../../../components/Table/TableComponent'
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker'
import SearchIcon from '@mui/icons-material/Search'
import { TableRow, TableCell } from '@mui/material'

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config'
import ChartButton from '../../../components/Button/ChartButton'
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import { ClearButton } from '../../../components/Button/iconbutton'
import TablePaginationComponent from '../../../components/TablePaginationComponent'
import Chart from "../../../components/Chart";
import { LipidProfile, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface'

const LipidProfileForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [lipidProfiles, setLipidProfiles] = useState<LipidProfile[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [showChart, setShowChart] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success')
  const [showClearFilterButton, setShowClearFilterButton] = useState(false)

  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  useEffect(() => {
    fetchLipidProfileRecords()
    setShowClearFilterButton(!!(searchTerm !== '' || (dateRange.startDate && dateRange.endDate)))
  }, [page, rowsPerPage, searchTerm, dateRange])



  // Assuming the LipidProfile interface is already defined as provided in your previous messages

  const fetchLipidProfileRecords = async (startDate: string = '', endDate: string = '') => {
    try {
      const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
        params: {
          limit: rowsPerPage, // Define this in your component state
          page: page + 1, // Current page number
          from: startDate, // Start date filter
          to: endDate, // End date filter
          order_by: 'details->date', // Sort order
          dir: 2, // Sorting direction
          user_id: patientId, // Patient ID for the records
          slug: 'lipid-profile', // Specific slug for lipid profile
          searchkey: searchTerm || '' // Search term for filtering
        },
        headers: getAxiosConfig().headers // Configure axios headers
      });

      const vitalsData = response.data.data.vitals;
      const total = response.data.data.pagination?.total;

      if (Array.isArray(vitalsData) && vitalsData.length > 0) {
        const formattedData: LipidProfile[] = vitalsData.map((vital) => ({
          id: vital.id,
          user_id: vital.user_id,
          consult_id: vital.consult_id,
          peripheral_id: vital.peripheral_id,
          slug: vital.slug,
          details: {
            hdl: vital.details.hdl,
            ldl: vital.details.ldl,
            date: vital.details.date,
            time: vital.details.time,
            vldl: vital.details.vldl,
            total: vital.details.total,
            ldl_hdl: vital.details.ldl_hdl,
            hdl_unit: vital.details.hdl_unit,
            ldl_unit: vital.details.ldl_unit,
            vldl_unit: vital.details.vldl_unit,
            total_unit: vital.details.total_unit,
            hdl_message: vital.details.hdl_message,
            ldl_message: vital.details.ldl_message,
            vldl_message: vital.details.vldl_message,
            total_message: vital.details.total_message,
            triglycerides: vital.details.triglycerides,
            hdl_range_code: vital.details.hdl_range_code,
            ldl_range_code: vital.details.ldl_range_code,
            hdl_ldl_message: vital.details.hdl_ldl_message,
            vldl_range_code: vital.details.vldl_range_code,
            hdl_message_flag: vital.details.hdl_message_flag,
            ldl_message_flag: vital.details.ldl_message_flag,
            total_range_code: vital.details.total_range_code,
            vldl_message_flag: vital.details.vldl_message_flag,
            hdl_ldl_range_code: vital.details.hdl_ldl_range_code,
            total_message_flag: vital.details.total_message_flag,
            triglycerides_unit: vital.details.triglycerides_unit,
            hdl_ldl_message_flag: vital.details.hdl_ldl_message_flag,
            triglycerides_message: vital.details.triglycerides_message,
            triglycerides_range_code: vital.details.triglycerides_range_code,
            triglycerides_message_flag: vital.details.triglycerides_message_flag,
          },
          freeze: vital.freeze,
          document: vital.document,
        }));

        // Set the fetched lipid profiles and total records
        setLipidProfiles(formattedData); // Assign formatted data to lipid profiles
        setTotalRecords(total ?? 0); // Assuming this state setter is defined
      } else {
        // Handle case when there are no records
        setLipidProfiles([]); // Clear the state
        setTotalRecords(0); // Reset total records
      }
    } catch (error) {
      console.error('Error fetching lipid profile records:', error); // Error logging
    } finally {
      setLoading(false); // Set loading to false after the fetch
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('')
    setDateRange({ startDate: '', endDate: '' })
    setPage(0) // Reset to the first page
    setRowsPerPage(5) // Reset rows per page if needed
    fetchLipidProfileRecords() // Fetch all records again
    setShowClearFilterButton(false) // Hide the clear button
  }


  const renderRow = (item: LipidProfile, index: number) => {
    const { details } = item;

    return (
      <TableRow key={index}>
        <TableCell className="px-4 py-3 font-jost sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}">{page * rowsPerPage + index + 1}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
          {details?.date || 'N/A'} {details?.time || 'N/A'}
        </TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{details?.ldl?.toString() || 'N/A'} mg/dL</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{details?.hdl?.toString() || 'N/A'} mg/dL</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{details?.vldl?.toString() || 'N/A'} mg/dL</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{details?.ldl_hdl?.toString() || 'N/A'} mg/dL</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{details?.triglycerides?.toString() || 'N/A'} mg/dL</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{details?.total?.toString() || 'N/A'} mg/dL</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
          <span
            style={{
              backgroundColor: details?.total_range_code || '#ccc', // Provide a default color
              padding: '4px 8px',
              borderRadius: '8px',
              color: '#FFFFFF'
            }}
          >
            {details?.total_message || 'N/A'}
          </span>
        </TableCell>
           
      </TableRow>
    );
  };


  const chartData = {
    labels: lipidProfiles.map((record) => `${record.details?.date} ${record.details?.time}`),
    datasets: [
      {
        label: 'LDL (mg/dL)',
        data: lipidProfiles.map((record) => record.details?.ldl ?? 0),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 1
      },
      {
        label: 'HDL (mg/dL)',
        data: lipidProfiles.map((record) => record.details?.hdl ?? 0),
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        borderWidth: 1
      },
      {
        label: 'VLDL (mg/dL)',
        data: lipidProfiles.map((record) => record.details?.vldl ?? 0),
        borderColor: 'rgba(255, 159, 64, 1)',
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        borderWidth: 1
      },
      {
        label: 'LDL/HDL (mg/dL)',
        data: lipidProfiles.map((record) => record.details?.ldl_hdl ?? 0),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderWidth: 1
      },
      {
        label: 'Triglycerides (mg/dL)',
        data: lipidProfiles.map((record) => record.details?.triglycerides ?? 0),
        borderColor: 'rgba(255, 206, 86, 1)',
        backgroundColor: 'rgba(255, 206, 86, 0.2)',
        borderWidth: 1
      },
      {
        label: 'Total Cholesterol (mg/dL)',
        data: lipidProfiles.map((record) => record.details?.total ?? 0),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 1
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw}`
        }
      }
    }
  }

  return (
    <div>
      <div className="flex items-end justify-between w-full gap-12">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId="LipidProfile_Start_Date"
            endDateId="LipidProfile_End_Date"
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates)
            }}
            value={dateRange}
          />
          <IconButton
            icon={<SearchIcon />}
            onClick={() => fetchLipidProfileRecords(dateRange.startDate, dateRange.endDate)}
          />
          {showClearFilterButton && <ClearButton onClick={handleClearFilters} />}
        </div>
        <div className="flex justify-end space-x-4">
        <ChartButton
          text={showChart ? 'List' : 'Chart'}
          onClick={() => setShowChart((prev) => !prev)}
        /></div>
      
      </div>
    
      {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
          <TableComponent
            headers={[
              'S.No',
              'Date & Time',
              'LDL (mg/dL)',
              'HDL (mg/dL)',
              'VLDL (mg/dL)',
              'LDL/HDL (mg/dL)',
              'Triglycerides (mg/dL)',
              'Total Cholesterol (mg/dL)',
              'Result',
             
            ]}
            data={lipidProfiles}
            renderRow={renderRow} error={''} />
            </>
            )}
               <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
        
    
    </div>
  )
}

export default LipidProfileForm
